import React, { useRef, useState, useCallback, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { Box, Tab, Tabs } from "@mui/material";
import { MyDocumentsDataPanel } from "my-documents/components";
import {
  MyHrContactsDataPanel,
  MyHrContractCard,
  MyHrContractsDataPanel,
  MyHrPayrollDataPanel,
  MyHrPersonalDataPanel,
  MyHrStickyPersonalDataHeader,
} from "my-hr/components";
import {
  useMyHrChangeRequests,
  useMyHrChangeRequestsExaminersName,
  useMyHrContractsByEmployee,
  useMyHrEmployee,
  useMyHrUserRolesDataByContract,
} from "my-hr/hooks";
import { MyTaxApplicationsDataPanel } from "my-tax/components/applications-data-panel";
import {
  OcFormEditor,
  OcPageNameTitle,
  OcPageTransition,
  OcTabPanel,
} from "oc/components";
import {
  countryOfBirthDialogDef,
  dateOfBirthDialogDef,
  emailsOtherDialogDef,
  emailsWorkDialogDef,
  genderDialogDef,
  identityCardNumberDialogDef,
  invalidateAddressDialogDef,
  maritalStatusDialogDef,
  mothersNameDialogDef,
  nameDialogDef,
  nationalityDialogDef,
  permanentAddressDialogDef,
  phonesPrivateDialogDef,
  phonesWorkDialogDef,
  phonesWorkMobileDialogDef,
  placeOfBirthDialogDef,
  serviceAddressDialogDef,
  socialSecurityNumberDialogDef,
  taxNumberDialogDef,
  temporaryAddressDialogDef,
} from "oc/components/form-editor/configs";
import {
  useOcCurrentUserState,
  useOcCustomerLicences,
  useOcIsMobile,
  useOcProperty,
  useOcClientState
} from "oc/hooks";
import {
  ocScrollTriggerState,
  ocSelectedTabState,
} from "states";

export const MyHRMyInfoPage = () => {
  const isMobile = useOcIsMobile();
  const { t, i18n } = useTranslation();
  const [selectedTab, setSelectedTab] = useRecoilState(
    ocSelectedTabState("MyHrUserHomePage"),
  );

  type DialogDef = any;
  const [def, setDef] = useState<DialogDef | null>(null);
  const [modifier, setModifier] = useState(null);
  const { licences } = useOcCustomerLicences();

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  let { contractCode, employeeCode } = useOcCurrentUserState();

  const { data: employeeData } = useMyHrEmployee({ employeeId: employeeCode });
  const { data: contractsData } = useMyHrContractsByEmployee({
    employee: employeeCode,
  });

  const { data: examinersData, loading: examinersLoading } =
    useMyHrChangeRequestsExaminersName({
      locale: i18n.language,
      contract: contractCode,
    });

  const { data: myDocumentsMenuEnabled } = useOcProperty({
    resource: "myDocuments",
    key: "myDocumentsMenuEnabled",
  });

  const myDocumentsEnabled = myDocumentsMenuEnabled === "false" ? false : true;

  const { data: taxApplicationsMenuEnabled } = useOcProperty({
    resource: "system",
    key: "taxApplicationsMenuEnabled",
  });

  const taxApplicationsEnabled = taxApplicationsMenuEnabled === "false" ? false : true;

  const { data: payrollMenuEnabled } = useOcProperty({
    resource: "system",
    key: "payrollMenuEnabled",
  });

  const payrollEnabled = payrollMenuEnabled === "false" ? false : true;

  const { data: rolesData } = useMyHrUserRolesDataByContract({
    contract: contractCode,
  });

  const isExaminer = rolesData?.find(
    (item) => item.product === "MyHR" && item.role === "examiner",
  )
    ? true
    : false;

  const isUploader = rolesData?.find(
    (item) => item.product === "MyDocuments" && item.role === "uploader",
  )
    ? true
    : false;

  const hasExaminer = examinersData?.length > 0 && !examinersLoading;

  let isEditButtonDisabled = !hasExaminer;
  if (isExaminer && !hasExaminer) {
    isEditButtonDisabled = false;
  }

  const { data: changeRequestsData } = useMyHrChangeRequests(employeeCode);

  let selectedContract = contractsData?.find(
    (item: { code: string }) => item.code === contractCode,
  );


  const pageRef = useRef<HTMLDivElement>(null);

  const [scrollTrigger, setScrollTrigger] = useRecoilState(ocScrollTriggerState);
  const [pageWidth, setPageWidth] = useOcClientState<number>("pageWidth", 0);

  const { data: changeSettingsEnabled } = useOcProperty({
    resource: "system",
    key: "changeRequestsEnabled",
    fetchPolicy: "network-only",
  });

  const enabledEdit = changeSettingsEnabled === "true" ? true : false;

  const handleScroll = useCallback(() => {
    const triggerY = 100;
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    setScrollTrigger(scrollTop >= triggerY ? "show" : "hide");
  }, [setScrollTrigger]);

  const handleResize = useCallback(() => {
    const width = pageRef.current?.clientWidth;
    if (width && width !== pageWidth) {
      setPageWidth(width);
    }
  }, [pageWidth, setPageWidth]);

  useLayoutEffect(() => {
    handleResize();
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [handleScroll, handleResize]);

  const handleCloseFormEditor = () => {
    setDef(null);
    setModifier(null);
  };

  const handleShowFormEditor = (type: string, modifier: any) => {
    if (modifier) {
      setModifier(modifier);
    }
    switch (type) {
      case "emails":
        setDef(
          modifier === "work" ? emailsWorkDialogDef : emailsOtherDialogDef,
        );
        break;
      case "phones":
        if (modifier === "work") setDef(phonesWorkDialogDef);
        else if (modifier === "workMobile") setDef(phonesWorkMobileDialogDef);
        else setDef(phonesPrivateDialogDef);
        break;
      case "mothersName":
        setDef(mothersNameDialogDef);
        break;
      case "name":
        setDef(nameDialogDef);
        break;
      case "taxNumber":
        setDef(taxNumberDialogDef);
        break;
      case "socialSecurityNumber":
        setDef(socialSecurityNumberDialogDef);
        break;
      case "identityCardNumber":
        setDef(identityCardNumberDialogDef);
        break;
      case "dateOfBirth":
        setDef(dateOfBirthDialogDef);
        break;
      case "placeOfBirth":
        setDef(placeOfBirthDialogDef);
        break;
      case "gender":
        setDef(genderDialogDef);
        break;
      case "maritalStatus":
        setDef(maritalStatusDialogDef);
        break;
      case "countryOfBirth":
        setDef(countryOfBirthDialogDef);
        break;
      case "nationality":
        setDef(nationalityDialogDef);
        break;
      case "permanentAddress":
        setDef(permanentAddressDialogDef);
        break;
      case "temporaryAddress":
        setDef(temporaryAddressDialogDef);
        break;
      case "serviceAddress":
        setDef(serviceAddressDialogDef);
        break;
      case "invalidateAddress":
        setDef(null);
        setDef(invalidateAddressDialogDef);
        break;
      case "newAddress": {
        const newAddress = { isNewAddress: true };
        if (!employeeData?.permanentAddress?.city) {
          setDef({ ...permanentAddressDialogDef, ...newAddress });
          break;
        }
        if (!employeeData?.temporaryAddress?.city) {
          setDef({ ...temporaryAddressDialogDef, ...newAddress });
          break;
        }
        if (!employeeData?.serviceAddress?.city) {
          setDef({ ...serviceAddressDialogDef, ...newAddress });
          break;
        }
        break;
      }
      default:
        break;
    }
  };

  const hasChangeRequest = ({
    type,
    typeGroup,
  }: {
    type: string;
    typeGroup?: string;
  }) => {
    if (isEditButtonDisabled) return true;
    if (typeGroup) {
      let isEdited = changeRequestsData?.find(
        (item: any) =>
          item.typeGroup === typeGroup && !item.approvedAt && !item.rejectedAt,
      );
      isEdited = isEdited ? true : false;
      let isDeleted = changeRequestsData?.find(
        (item: any) =>
          item.type === typeGroup && !item.approvedAt && !item.rejectedAt,
      );
      isDeleted = isDeleted ? true : false;
      return isEdited || isDeleted;
    }
    if (type === "mothersName") {
      let isDisabled = changeRequestsData?.find(
        (item: any) =>
          item.typeGroup === type && !item.approvedAt && !item.rejectedAt,
      );
      return isDisabled ? true : false;
    }

    let isDisabled = changeRequestsData?.find(
      (item: any) => item.type === type && !item.approvedAt && !item.rejectedAt,
    );
    const result = isDisabled ? true : false;
    return result;
  };

  return (
    <OcPageTransition>
      <Box ref={pageRef} minWidth={isMobile ? "auto" : "1024px"}>
        <MyHrStickyPersonalDataHeader
          pageWidth={pageWidth}
          employee={employeeData}
          contract={selectedContract}
          trigger={scrollTrigger === "show" ? true : false}
        />
        <OcPageNameTitle title={t("MY_HR_MYINFO_PAGE_TITLE")} goBack={false} />
        <Box id="personal-data-page" display="flex" flexDirection="column">
          <MyHrContractCard
            employee={employeeData}
            contract={selectedContract}
          />
          <Box padding={isMobile ? "0px" : "32px 24px 0px 24px"}>
            <Box bgcolor="#E3E5E5">
              <Tabs
                value={selectedTab}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                onChange={handleChangeTab}
                aria-label="disabled tabs example"
              >
                <Tab label={t("MY_HR_PERSONAL_DATA")} id="personalDataPanel" />
                <Tab label={t("MY_HR_CONTACTS")} id="contactsPanel" />
                {payrollEnabled && (
                  <Tab label={t("MY_HR_PAYROLL")} id="payrollPanel" />
                )}
                <Tab label={t("MY_HR_GET_CONTRACT")} id="contractPanel" />
                {taxApplicationsEnabled && (
                  <Tab label={t("MY_HR_APPLICATIONS")} id="applicationsPanel" />
                )}
                {myDocumentsEnabled && licences?.myDocuments && (
                  <Tab
                    label={t("MY_DOCUMENTS_DOCUMENTS")}
                    id="documentsPanel"
                  />
                )}
              </Tabs>
            </Box>
            <OcTabPanel value={selectedTab} index={0} style={{ padding: 0 }}>
              <MyHrPersonalDataPanel
                employee={employeeData}
                onEdit={handleShowFormEditor}
                // @ts-ignore
                hasChangeRequest={hasChangeRequest}
                editable={enabledEdit}
              />
            </OcTabPanel>
            <OcTabPanel value={selectedTab} index={1}>
              <MyHrContactsDataPanel
                employee={employeeData}
                onEdit={handleShowFormEditor}
                // @ts-ignore
                hasChangeRequest={hasChangeRequest}
                editable={enabledEdit}
              />
            </OcTabPanel>
            {payrollEnabled && (
              <OcTabPanel value={selectedTab} index={2}>
                <MyHrPayrollDataPanel contract={selectedContract} />
              </OcTabPanel>
            )}
            <OcTabPanel value={selectedTab} index={payrollEnabled ? 3 : 2}>
              <MyHrContractsDataPanel contracts={contractsData} />
            </OcTabPanel>
            {taxApplicationsEnabled && (
              <OcTabPanel value={selectedTab} index={payrollEnabled ? 4 : 3}>
                <MyTaxApplicationsDataPanel
                  employee={employeeData}
                  label={t("MY_TAX_AVAILABLE_APPLICATIONS")}
                  isMobile={isMobile}
                />
              </OcTabPanel>
            )}
            {myDocumentsEnabled && licences?.myDocuments && (
              <OcTabPanel value={selectedTab} index={
                (payrollEnabled ? 1 : 0) +
                (taxApplicationsEnabled ? 1 : 0) +
                3
              }>
                <MyDocumentsDataPanel
                  isUser={true}
                  contractCode={contractCode}
                  isUploader={isUploader}
                />
              </OcTabPanel>
            )}
          </Box>
        </Box>
        {def && (
          <OcFormEditor
            open={true}
            onClose={handleCloseFormEditor}
            data={employeeData}
            def={def}
            onShowFormEditor={handleShowFormEditor}
            modifier={modifier ? modifier : undefined}
          />
        )}
      </Box>
    </OcPageTransition>
  );
};

export default MyHRMyInfoPage;
